import type { Status } from "../types/domain";

/** 48210 -> "48.2K", 176280 -> "176.3K". The mockup's own fmtN. */
export function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return String(n);
}

const BLOCKS = "▁▂▃▄▅▆▇█";

/** Renders a series as a block sparkline, exactly as the mockup does. */
export function spark(values: number[]): string {
  if (values.length === 0) return "";
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  return values
    .map((v) => BLOCKS[Math.min(7, Math.round(((v - min) / span) * 7))])
    .join("");
}

/** A proportional bar drawn in block characters (cache hierarchy, pressure). */
export function bar(fraction: number, width: number): string {
  const filled = Math.max(0, Math.min(width, Math.round(fraction * width)));
  return "█".repeat(filled) + "░".repeat(width - filled);
}

/** Maps a status to the palette variable the mockup paints it with. */
export function statusColor(status: Status | string): string {
  switch (status) {
    case "ready":
    case "committed":
    case "done":
    case "healthy":
      return "var(--vf-ok)";
    case "idle":
      return "var(--vf-sec)";
    case "indexing":
    case "running":
    case "compacting":
      return "var(--vf-warn)";
    case "queued":
      return "var(--vf-acc)";
    case "failed":
    case "error":
    case "stale":
      return "var(--vf-bad)";
    case "canceled":
      return "var(--vf-dim)";
    default:
      return "var(--vf-sec)";
  }
}

export function bytes(n: number): string {
  if (!n || n <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(i === 0 ? 0 : 1).replace(/\.0$/, "")} ${units[i]}`;
}

export function percent(fraction: number): string {
  if (!fraction) return "—";
  return `${Math.round(fraction * 100)}%`;
}

export function dimsLabel(dims: number): string {
  return dims ? `${dims}d` : "BM25";
}

/** Flattens a metadata object into the `key=value` chips the mockup shows. */
export function metaPairs(meta: Record<string, unknown>): { k: string; v: string }[] {
  return Object.entries(meta).map(([k, v]) => ({ k, v: String(v) }));
}

/** Pretty-prints a request/response body for the JSON panes. */
export function jsonPretty(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
